/*!
 * @license
 * SPDX-License-Identifier: LicenseRef-cloudEQ-Proprietary
 * Copyright (c) 2026-present cloudEQ Inc. All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL.
 *
 * This source code, including all associated files, documentation, and
 * compiled forms, is the exclusive property of cloudEQ Inc. and is
 * protected by copyright, trade secret, and other intellectual property laws.
 *
 * This software is licensed, not sold, solely for use by authorized
 * licensees under the terms of a written license agreement with
 * cloudEQ Inc. Unless expressly permitted by that agreement, you may not
 * copy, modify, merge, distribute, sublicense, sell, publish, reverse
 * engineer, decompile, disassemble, or create derivative works of this
 * software, in whole or in part, by any means.
 *
 * Access to this file does not grant any license or right to use it.
 * Unauthorized use, reproduction, or disclosure is strictly prohibited and
 * may result in civil and criminal penalties.
 *
 * Contact: legal@cloudeq.com
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Task, TaskList, type VerticalId } from '@/types/task';

/**
 * The board's data.
 *
 * **The circle is part of the key.** Every task request is already circle-scoped
 * on the server — a circle you cannot read quietly resolves to one you can — and
 * putting that in the key is what stops one circle's cached list ever being shown
 * for another after a switch.
 *
 * `archived` is a separate list, not a filter over this one: the server decides
 * what "archived" means (hand-archived *or* aged out of Complete past the
 * retention cutoff) and the client must not try to re-derive it.
 */
export const taskKeys = {
  all: ['tasks'] as const,
  board: (circleId: string) => ['tasks', 'board', circleId] as const,
  archived: (circleId: string) => ['tasks', 'archived', circleId] as const,
};

function withCircle(path: string, circleId: string) {
  return circleId ? `${path}${path.includes('?') ? '&' : '?'}circle=${encodeURIComponent(circleId)}` : path;
}

export function useBoardTasks(circleId: string, enabled = true) {
  return useQuery({
    queryKey: taskKeys.board(circleId),
    queryFn: async () => TaskList.parse(await api.get(withCircle('/api/tasks', circleId))),
    enabled,
  });
}

export function useArchivedTasks(circleId: string, enabled = true) {
  return useQuery({
    queryKey: taskKeys.archived(circleId),
    queryFn: async () => TaskList.parse(await api.get(withCircle('/api/tasks?archived=1', circleId))),
    enabled,
    // Arriving on this view asks the server again, which is what `setView` has
    // always done — `if (v === 'archived') loadArchivedTasks()`. It matters more
    // here than on the board: nothing polls the archive, and what has aged out
    // of Complete is decided by a cutoff at read time, so a cached answer can be
    // stale in a way no mutation of yours would invalidate.
    refetchOnMount: 'always',
  });
}

/**
 * Moving a card to another column.
 *
 * Optimistic, and the rollback is the point rather than a nicety: a card that
 * snaps back to where it was, with a message, is honest; a card that sits in the
 * new column while the server never accepted the move is a lie the person only
 * discovers on their next reload.
 *
 * Two details that are easy to get wrong:
 *
 *  - **Cancel in-flight refetches first.** The 20-second poll can land between
 *    the optimistic write and the response and put the old column straight back.
 *  - **Do not invalidate on success.** The server's reply is the task; it goes
 *    into the cache as-is. Invalidating would refetch the whole board to learn
 *    something already in hand — and would flash on a slow connection.
 */
export function useMoveTask(circleId: string) {
  const qc = useQueryClient();
  const key = taskKeys.board(circleId);

  return useMutation({
    mutationFn: async ({ id, vertical }: { id: string; vertical: VerticalId }) =>
      Task.parse(await api.patch(`/api/tasks/${id}/vertical`, { vertical })),

    onMutate: async ({ id, vertical }) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<Task[]>(key);
      qc.setQueryData<Task[]>(key, (old) =>
        (old ?? []).map((t) => (t.id === id ? { ...t, vertical } : t)),
      );
      return { previous };
    },

    onError: (_err, _vars, context) => {
      if (context?.previous) qc.setQueryData(key, context.previous);
    },

    onSuccess: (moved) => {
      qc.setQueryData<Task[]>(key, (old) => (old ?? []).map((t) => (t.id === moved.id ? moved : t)));
    },
  });
}
